/**
 * Time Override — QA tester clock-shift helper.
 *
 * Stores a signed second-offset in localStorage. The offset is sent as an
 * `X-Time-Offset` header on every API request (see api.ts) so the backend
 * can shift Carbon::now() to match what the tester sees.
 *
 * Gated by VITE_APP_ENV and an explicit VITE_ENABLE_TIME_OVERRIDE flag so
 * the feature is a hard no-op outside local / qa / uat builds.
 */

const ENABLED_ENVS = ['local', 'qa', 'uat']
const STORAGE_KEY = '__time_override_offset'
const EVENT = 'time-override:change'

const appEnv = String(import.meta.env.VITE_APP_ENV ?? '').toLowerCase()
const explicit = String(import.meta.env.VITE_ENABLE_TIME_OVERRIDE ?? '').toLowerCase()
const envAllows = ENABLED_ENVS.includes(appEnv)
const explicitDisabled = explicit === 'false' || explicit === '0'
const explicitEnabled = explicit === 'true' || explicit === '1'

const enabled =
  (explicitEnabled || (envAllows && !explicitDisabled)) &&
  typeof window !== 'undefined'

export function isTimeOverrideEnabled(): boolean {
  return enabled
}

export function getTimeOverrideOffsetSeconds(): number {
  if (!enabled) return 0
  const raw = window.localStorage.getItem(STORAGE_KEY)
  if (!raw) return 0
  const parsed = Number.parseInt(raw, 10)
  return Number.isFinite(parsed) ? parsed : 0
}

export function setTimeOverrideOffsetSeconds(offset: number): void {
  if (!enabled) return
  const safe = Math.trunc(Number.isFinite(offset) ? offset : 0)
  if (safe === 0) {
    window.localStorage.removeItem(STORAGE_KEY)
  } else {
    window.localStorage.setItem(STORAGE_KEY, String(safe))
  }
  window.dispatchEvent(new CustomEvent(EVENT))
}

export function clearTimeOverride(): void {
  setTimeOverrideOffsetSeconds(0)
}

/**
 * Real wall-clock now in ms — never shifted. Use this for things that must
 * never be fooled (rate limiting, performance timing, animation cadence).
 */
export function realNowMs(): number {
  return Date.now()
}

/** Shifted now in ms = real now + offset * 1000. Use in UI rendering. */
export function shiftedNowMs(): number {
  return Date.now() + getTimeOverrideOffsetSeconds() * 1000
}

/** Shifted Date instance — convenience for components. */
export function shiftedNow(): Date {
  return new Date(shiftedNowMs())
}

/** Subscribe to override changes (for badges, panels, etc). */
export function onTimeOverrideChange(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener(EVENT, listener)
  window.addEventListener('storage', listener)
  return () => {
    window.removeEventListener(EVENT, listener)
    window.removeEventListener('storage', listener)
  }
}
